import { translate } from "@/i18n";
import { desktopVoiceMessages } from "@/i18n/messages/desktop-voice";
import { listen } from "@tauri-apps/api/event";
import { adoptScreenRecordingSessionIntoText } from "./screen-recording-store";
import { withMutationEmitSuppressed } from "@/lib/mirrored-store";
import type { ImageAttachment } from "@/components/panels/image-preview-strip";
import { requestVoiceWakeSleep } from "./voice-wake-hooks";
import { announceVoiceChatCommand } from "./voice-command-feedback-store";
import { track } from "./observability";
import { getWindowDisplayId, getWindowRoleSync } from "./use-window-role";
import {
  type SpeechSpan,
  stripTtsEchoFromOneShotFinal,
} from "./continuous-voice-transcript-filter";
import {
  captureCommentScreenshots,
  takeCommentDictationClaim,
} from "./comment-dictation-target";
import {
  consumeSuppressedOneShotFinal,
  isContinuousCaptureAlive,
} from "./continuous-voice-store";
import { dispatchVoicePrompt } from "./voice-dispatch";
import {
  recordVoiceAttentionTranscript,
  useVoiceAttentionStore,
} from "./voice-attention-store";
import type { NativeTimedSpeechSegment } from "./voice-speech-timing";
import {
  dictationAttentionOriginDisplayId,
  dictationAttentionRecordingId,
  endDictationAttention,
} from "./dictation-attention";
import { recordDictationTranscriptDiagnostic } from "./dictation-transcript-diagnostics";
import {
  hasDictationOverrideHandler,
  runDictationOverride,
} from "./dictation-override";
import type { VoiceRequestToken } from "./voice-request-cancellation";
import {
  abandonProvisionalVoiceNewChatRouting,
  armPendingVoiceNewChatRouting,
  consumePendingVoiceNewChatIntent,
} from "./voice-new-chat-routing";
import { switchVoiceCli, waitForVoiceCliSwitch } from "./voice-cli-switch";
import {
  detectVoiceControlIntent,
  voiceCliRuntime,
} from "./voice-control-intents";
import { deliverDictationAnywhereFinal } from "./dictation-anywhere/dictation-anywhere-store";
import { observeDictationImageTiming } from "./dictation-anywhere/dictation-anywhere-image-references";
import type { DictationErrorKind } from "./dictation-error-guidance";

type FinalRoutingDictationState = {
  status: "idle" | "recording" | "processing" | "error";
  mode: "denker" | "anywhere";
  sessionId: string | null;
  guidePractice: boolean;
  lastError?: string;
  errorKind?: DictationErrorKind;
  lastFinalText?: string;
  partialText: string;
  screenshotIntent: boolean;
};

type FinalRoutingOptions = {
  getState: () => FinalRoutingDictationState & { cancel: () => Promise<void> };
  setState: (state: Partial<FinalRoutingDictationState>) => void;
  isCancelling: () => boolean;
  setCancelling: (value: boolean) => void;
  cancelRequest: () => void;
  captureRequest: () => VoiceRequestToken;
  recordedSpan: () => SpeechSpan;
  clearProcessingWatchdog: () => void;
  applyMirrorVisualState: (apply: () => void) => void;
  resetVolumeAndDeadMic: () => void;
  scheduleErrorClear: () => void;
  debug: (message: string) => void;
};

function shouldDispatchVoiceTranscript(isOwner: boolean): boolean {
  const activeDisplayId = dictationAttentionOriginDisplayId();
  if (activeDisplayId === null || activeDisplayId === undefined) {
    return isOwner;
  }
  return activeDisplayId === getWindowDisplayId();
}

/**
 * Routes one-shot `voice-transcript` events: partials into the store, and a
 * final to dictation anywhere, the override handler, the claimed composer, a
 * voice-control intent or the agent — in that order.
 */
export function attachDictationFinalRouting(
  options: FinalRoutingOptions,
): void {
  void listen<{
    kind: "partial" | "commit" | "final";
    text: string;
    /** The text before the vocabulary corrected it; only when it did. */
    uncorrected_text?: string;
    duration_ms?: number;
    speech_segments?: NativeTimedSpeechSegment[];
    capture_id?: string;
    session_id?: string;
  }>("voice-transcript", (event) => {
    // An earlier one-shot recording's transcript - a final that outlived the
    // transcript watchdog - belongs to that recording's mode, not this one's:
    // routed now, an anywhere final could reach the agent or a Denker final
    // the app in front. The current recording's late final still routes.
    const sessionId = event.payload.session_id;
    if (sessionId && sessionId !== options.getState().sessionId) {
      options.debug("voice-transcript dropped: earlier recording");
      return;
    }
    const isOwner = getWindowRoleSync() === "owner";
    const { kind, text } = event.payload;
    // Dictation anywhere keeps what was said out of every store: no
    // attention timeline, no mirrored partial, no lastFinalText.
    const anywhere = options.getState().mode === "anywhere";
    if (
      anywhere &&
      isOwner &&
      !isContinuousCaptureAlive() &&
      !event.payload.capture_id &&
      !options.isCancelling()
    ) {
      observeDictationImageTiming(kind, text, event.payload.speech_segments);
    }
    if (!isContinuousCaptureAlive() && !anywhere) {
      recordVoiceAttentionTranscript(kind, text, event.payload.speech_segments);
    }
    if (!isContinuousCaptureAlive() && !anywhere) {
      const nextPartial = kind === "final" ? "" : text;
      if (options.getState().partialText !== nextPartial) {
        if (
          kind === "final" ||
          typeof document === "undefined" ||
          !document.hidden
        ) {
          options.setState({ partialText: nextPartial });
        } else {
          // Backgrounded webview: keep local state fresh (reopen paints the
          // latest text in one pass) but skip the cross-window mirror emit
          // per interim chunk — the queued emits otherwise reconcile in a
          // single janky burst on foreground. Finals always emit so mirrors
          // converge.
          withMutationEmitSuppressed(() => {
            options.setState({ partialText: nextPartial });
          });
        }
      }
    }
    const diagnostic = recordDictationTranscriptDiagnostic(
      kind,
      text,
      event.payload.duration_ms ?? 0,
    );
    if (!diagnostic) return;
    options.debug(diagnostic);

    if (isContinuousCaptureAlive()) {
      return;
    }
    if (consumeSuppressedOneShotFinal()) {
      options.cancelRequest();
      options.setState({ status: "idle", screenshotIntent: false });
      options.resetVolumeAndDeadMic();
      return;
    }
    // A capture_id tags continuous-session transcripts. One-shot finals are
    // never tagged, so anything tagged that reaches this point — e.g. the
    // settlement final flushed while a failed continuous capture shuts
    // down — must not dispatch as a one-shot result.
    // Windows coupling: windows_stt.rs `emit_transcript` stamps events from
    // the live session slot (Stamp::Active), and only `start_dictation`'s
    // `windows_voice_session::clear()` (lib.rs) keeps one-shot finals
    // untagged. Changes to Windows stamping must preserve that, or this
    // guard starts dropping real one-shot prompts.
    if (event.payload.capture_id) {
      return;
    }

    options.clearProcessingWatchdog();
    if (anywhere) {
      routeDictationAnywhereFinal(options, text, isOwner);
      return;
    }
    const shouldDispatch = shouldDispatchVoiceTranscript(isOwner);
    if (!shouldDispatch) {
      options.applyMirrorVisualState(() => {
        options.setState({
          status: "idle",
          lastFinalText: text.trim(),
          screenshotIntent: false,
        });
        options.resetVolumeAndDeadMic();
      });
      return;
    }

    if (options.isCancelling()) {
      options.setCancelling(false);
      abandonProvisionalVoiceNewChatRouting("cancelled_final");
      options.setState({ status: "idle" });
      options.resetVolumeAndDeadMic();
      return;
    }
    const requestToken = options.captureRequest();
    // This turn's own attention session: async branches end only it, never
    // one a later recording (e.g. dictation anywhere) has since begun.
    const attentionId =
      dictationAttentionRecordingId() ??
      useVoiceAttentionStore.getState().recordingId;
    // Read before going idle, which is when a mic owner releases its claim.
    const commentTarget = takeCommentDictationClaim();

    // Control phrases ("go to sleep", "stop listening") are ones Denker
    // itself teaches, so they can look like echo — never strip them.
    const rawTrimmed = text.trim();
    const trimmed = detectVoiceControlIntent(rawTrimmed)
      ? rawTrimmed
      : stripTtsEchoFromOneShotFinal(
          rawTrimmed,
          options.recordedSpan(),
          event.payload.uncorrected_text,
        );
    options.setState({ status: "idle", lastFinalText: trimmed });
    options.resetVolumeAndDeadMic();
    const { startedAt } = options.recordedSpan();
    track("desktop_voice_dictation_stopped", {
      duration_ms: startedAt ? Date.now() - startedAt : 0,
      transcript_length: trimmed.length,
      had_text: trimmed.length > 0,
    });
    if (!trimmed) {
      abandonProvisionalVoiceNewChatRouting("empty_final");
      requestToken.finish();
      endDictationAttention(attentionId);
      // Nothing heard is not a failure: the attempt simply ends.
      return;
    }

    const attentionState = useVoiceAttentionStore.getState();

    if (hasDictationOverrideHandler()) {
      abandonProvisionalVoiceNewChatRouting("dictation_override");
      try {
        runDictationOverride(trimmed);
      } catch (e) {
        console.warn("[dictation] override handler threw:", e);
      }
      requestToken.finish();
      endDictationAttention(attentionId);
      return;
    }

    if (commentTarget) {
      abandonProvisionalVoiceNewChatRouting("comment_dictation");
      void (async () => {
        try {
          if (requestToken.isCancelled()) return;
          let images: ImageAttachment[] = [];
          if (
            options.getState().screenshotIntent &&
            !commentTarget.disableScreenshotCapture
          ) {
            images = await captureCommentScreenshots();
          }
          if (requestToken.isCancelled()) return;
          if (commentTarget.id === "input-bar-target:standalone")
            adoptScreenRecordingSessionIntoText(attentionId);
          await commentTarget.deliverTranscript(trimmed, images);
        } catch (e) {
          console.warn("[dictation] comment insertion failed:", e);
          options.setState({ status: "error", lastError: String(e) });
          options.scheduleErrorClear();
        } finally {
          requestToken.finish();
          options.setState({ screenshotIntent: false });
          endDictationAttention(attentionId);
        }
      })();
      return;
    }

    // A turn recorded during the guide is practice, wherever and whenever its
    // final lands: a slow transcript can arrive after the guide has ended,
    // and sending it then would interrupt onboarding's own agent work.
    if (options.getState().guidePractice) {
      abandonProvisionalVoiceNewChatRouting("guide_practice");
      requestToken.finish();
      options.setState({ screenshotIntent: false });
      endDictationAttention(attentionId);
      return;
    }

    const voiceControl = detectVoiceControlIntent(trimmed);
    if (voiceControl?.intent === "sleep_listening") {
      void options
        .getState()
        .cancel()
        .then(() => requestVoiceWakeSleep());
      return;
    }
    let promptText = trimmed;
    if (voiceControl?.intent === "new_chat") {
      armPendingVoiceNewChatRouting();
      if (!voiceControl.residualText) {
        announceVoiceChatCommand(
          translate(desktopVoiceMessages, "commandNewChat"),
        );
        requestToken.finish();
        options.setState({ screenshotIntent: false });
        endDictationAttention(attentionId);
        return;
      }
      promptText = voiceControl.residualText;
    }

    void (async () => {
      try {
        const runtime = voiceControl && voiceCliRuntime(voiceControl.intent);
        if (runtime) {
          await switchVoiceCli(runtime);
          options.setState({ screenshotIntent: false });
          return;
        }
        await waitForVoiceCliSwitch();
        const newChatIntent = await consumePendingVoiceNewChatIntent();
        await dispatchVoicePrompt({
          text: promptText,
          source: "dictation",
          screenshotIntent: options.getState().screenshotIntent,
          ...(newChatIntent ?? {}),
          isCancelled: requestToken.isCancelled,
          attentionState,
        });
        options.setState({ screenshotIntent: false });
      } catch (e) {
        options.setState({ status: "error", lastError: String(e) });
        options.scheduleErrorClear();
      } finally {
        requestToken.finish();
        endDictationAttention(attentionId);
      }
    })();
  });
}

/**
 * A dictation anywhere final goes to the flow in the owner window and nowhere
 * else: never to the agent, a composer, the override handler, TTS-echo
 * stripping or voice commands. Other windows only settle their mirrored
 * status. A cancelled recording's final is swallowed. The flow takes the text
 * before the status settles, so it never mistakes the settle for a cancel.
 */
function routeDictationAnywhereFinal(
  options: FinalRoutingOptions,
  text: string,
  isOwner: boolean,
): void {
  const settle = () => {
    options.setState({ status: "idle", screenshotIntent: false });
    options.resetVolumeAndDeadMic();
  };
  if (!isOwner) {
    options.applyMirrorVisualState(settle);
    return;
  }
  const cancelled = options.isCancelling();
  options.setCancelling(false);
  options.cancelRequest();
  if (!cancelled) deliverDictationAnywhereFinal(text.trim());
  settle();
}
